import React, { useState, useEffect } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Select, MenuItem, InputLabel, FormControl
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search } from '@mui/icons-material';
import SimpleDatePicker from '../../../../ui/simpleDatePicker';
import InsuranceListDialog from '../../insurancePolicy/insuranceListDialog';
import { formatAmountForDisplayWithPreferences, formatDateWithTimezone } from 'utils';
import { usePreferences } from 'hooks/usePreferences';
import Can from 'components/auth/Can';

// Define possible claim statuses
const claimStatuses = ['Pending', 'Approved', 'Denied', 'Cancelled'];

// Helper component for the Add/Edit Insurance Claim Dialog
function AddEditClaimDialog({ open, onClose, claimData, onSave, customer, amountToClaim }) {
    const [provider, setProvider] = useState('');
    const [providerId, setProviderId] = useState(null); // Added for insurance provider ID
    const [claimAmount, setClaimAmount] = useState('');
    const [approvedAmount, setApprovedAmount] = useState('');
    const [fileDate, setDateFiled] = useState('');
    const [approvedDate, setDateApproved] = useState(''); // Null initially
    const [status, setStatus] = useState('Pending');
    const [notes, setNotes] = useState('');
    const [errors, setErrors] = useState({});
    const [insuranceProviderSelectOpen, setInsuranceProviderSelectOpen] = useState(false);
    const [originalClaimAmount, setOriginalClaimAmount] = useState(0); // Store the original claim amount to prevent exceeding it
    // Populate form when claimData changes (for editing)
    useEffect(() => {
        if (claimData) {
            setProvider(claimData.provider || '');
            setProviderId(claimData.providerId || null); // Set provider ID if available
            setClaimAmount(claimData.claimAmount || '');
            setApprovedAmount(claimData.approvedAmount || '');
            setDateFiled(claimData.fileDate ? formatDateWithTimezone(claimData.fileDate) : '');
            setDateApproved(claimData.approvedDate ? formatDateWithTimezone(claimData.approvedDate) : '');
            setStatus(claimData.status || 'Pending');
            setNotes(claimData.notes || '');
            // Store the original claim amount to validate against modifications
            setOriginalClaimAmount(claimData.claimAmount || 0);
        } else {
            // Reset form for adding new claim
            setProvider('');
            setProviderId(null); // Reset provider ID
            setClaimAmount(amountToClaim || '');
            setApprovedAmount('');
            setDateFiled('');
            setDateApproved('');
            setStatus('Pending');
            setNotes('');
            // Store the original claim amount from amountToClaim prop
            setOriginalClaimAmount(amountToClaim || 0);
        }
        setErrors({}); // Clear errors on open/data change
    }, [open, claimData, amountToClaim]);

    const handleAmountChange = (setter) => (event) => {
        const value = event.target.value;
        if (/^\d+(\.\d{0,2})?$/.test(value) || value === '') {
            setter(value);
        }
    };

    const handleApprovedAmountChange = (event) => {
        const value = event.target.value;
        if (!(/^\d+(\.\d{0,2})?$/.test(value) || value === '')) {
            return;
        }

        setApprovedAmount(value);

        // Keep status and totals in sync: any positive approved amount implies Approved.
        if (value !== '' && parseFloat(value) > 0 && status !== 'Approved') {
            setStatus('Approved');
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!provider) newErrors.provider = 'Insurance provider is required';
        if (claimAmount === '' || parseFloat(claimAmount) < 0) newErrors.claimAmount = 'Claim amount must be a non-negative number';
        // Validate that claim amount doesn't exceed the original claim amount
        if (claimAmount !== '' && parseFloat(claimAmount) > originalClaimAmount) {
            newErrors.claimAmount = `Claim amount cannot exceed the original amount of $${originalClaimAmount.toFixed(2)}`;
        }
        if (approvedAmount !== '' && parseFloat(approvedAmount) < 0) newErrors.approvedAmount = 'Approved amount must be a non-negative number'; // Approved amount can be empty
        if (!fileDate) newErrors.fileDate = 'Date filed is required';
        // Add validation if approvedDate is required for certain statuses

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validate()) {
            return;
        }

        const parsedApprovedAmount = approvedAmount === '' ? null : parseFloat(approvedAmount);
        const normalizedStatus = parsedApprovedAmount !== null && parsedApprovedAmount > 0
            ? 'Approved'
            : status;

        const savedClaim = {
            ...claimData, // Include id if editing
            provider,
            policyId: providerId, // Include provider ID
            claimAmount: parseFloat(claimAmount),
            approvedAmount: parsedApprovedAmount, // Store as null if empty
            fileDate: fileDate,
            approvedDate: approvedDate ? approvedDate : null,
            status: normalizedStatus,
            notes,
        };
        onSave(savedClaim);
    };

    const handleInsuranceSelect = (insurance) => {
        setProvider(insurance.provider.name);
        setProviderId(insurance.id); // Set the selected insurance ID
        setInsuranceProviderSelectOpen(false);
    }

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{claimData ? 'Edit Insurance Claim' : 'Add New Insurance Claim'}</DialogTitle>
            <DialogContent dividers>
                <TextField
                    label="Provider"
                    fullWidth
                    margin="normal"
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                    error={!!errors.provider}
                    helperText={errors.provider}
                    InputProps={{
                        endAdornment: (
                            <IconButton onClick={() =>  setInsuranceProviderSelectOpen(true)}>
                                <Search />
                            </IconButton>
                        ),
                    }}
                />

                <TextField
                    label="Claim Amount"
                    fullWidth
                    margin="normal"
                    value={claimAmount}
                    onChange={handleAmountChange(setClaimAmount)}
                    error={!!errors.claimAmount}
                    helperText={errors.claimAmount}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
                    type="text" 
                />

                <TextField
                    label="Approved Amount"
                    fullWidth
                    margin="normal"
                    value={approvedAmount}
                    onChange={handleApprovedAmountChange}
                    error={!!errors.approvedAmount}
                    helperText={errors.approvedAmount}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
                    type="text" inputMode="decimal"
                    placeholder="Leave empty if pending"
                />

                <SimpleDatePicker
                    label="Date Filed"
                    value={fileDate}
                    onChange={(value) => setDateFiled(value)}
                    size='large'
                    error={!!errors.fileDate}
                    helperText={errors.fileDate}
                />

                <SimpleDatePicker
                    label="Date Approved"
                    value={approvedDate}
                    size='large'
                    onChange={(value) => setDateApproved(value)}
                // Add error/helper text if needed based on validation rules
                />
                
                <FormControl fullWidth margin="normal" error={!!errors.status}>
                    <InputLabel id="claim-status-label">Status</InputLabel>
                    <Select
                        labelId="claim-status-label"
                        id="claim-status-select"
                        value={status}
                        label="Status"
                        onChange={(e) => setStatus(e.target.value)}
                    >
                        {claimStatuses.map((statusOption) => (
                            <MenuItem key={statusOption} value={statusOption}>{statusOption}</MenuItem>
                        ))}
                    </Select>
                    {errors.status && <Typography color="error" variant="caption">{errors.status}</Typography>}
                </FormControl>

                <TextField
                    label="Notes"
                    fullWidth
                    margin="normal"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    multiline
                    rows={2}
                />

            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">Save</Button>
            </DialogActions>

            <InsuranceListDialog
                    open={insuranceProviderSelectOpen}
                    onClose={() => setInsuranceProviderSelectOpen(false)}
                    onSelect={handleInsuranceSelect} 
                    customer={customer}
                />

        </Dialog>
    );
}

// Main Insurance Claim Collector Component
export default function InsuranceClaimCollector({
    claims = [], // Array of claim objects { id, provider, claimAmount, approvedAmount, fileDate, approvedDate, status, notes }
    customer,
    onAddClaim,
    onEditClaim,
    onDeleteClaim,
    amountToClaim
}) {
    const [openDialog, setOpenDialog] = useState(false);
    const [editingClaim, setEditingClaim] = useState(null); // null for add, claim object for edit
    const { preferences } = usePreferences();

    const handleOpenAddDialog = () => {
        setEditingClaim(null); // Ensure we are adding
        setOpenDialog(true);
    };

    const handleOpenEditDialog = (claim) => {
        setEditingClaim(claim); // Set claim data for editing
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleSaveClaim = (claim) => {
        if (claim.id || claim.tempId) {
            // Editing existing claim
            if (onEditClaim) {
                onEditClaim(claim);
            }
        } else {
            // Adding new claim
            const newClaim = { ...claim, tempId: Date.now() }; // Assign a simple unique ID
            if (onAddClaim) {
                onAddClaim(newClaim);
            }
        }        
        // if (claim.id) {
        //     // Editing existing claim
        //     if (onEditClaim) {
        //         onEditClaim(claim);
        //     }
        // } else {
        //     // Adding new claim
        //     const newClaim = { ...claim, id: Date.now() }; // Assign a simple unique ID
        //     if (onAddClaim) {
        //         onAddClaim(newClaim);
        //     }
        // }

        // if (onAddClaim) {
        //     onAddClaim(claim);
        // } else {
        //     throw new Error('onAddClaim function is not provided');
        // }

        handleCloseDialog(); // Close dialog after saving
    };

    const handleDeleteClaim = (claimId) => {
        // You might want a confirmation dialog here
        if (onDeleteClaim) {
            onDeleteClaim(claimId);
        }
    };

    // Calculate total approved amount from claims
    const totalInsuranceApproved = claims.reduce((sum, claim) => {
        // Only sum approved or paid claims
        if (claim.status === 'Approved' || claim.status === 'Paid') {
            return sum + (claim.approvedAmount || 0);
        }
        return sum;
    }, 0);


    return (
        <Box sx={{ mt: 1, border: '1px solid #ccc', p: 1, borderRadius: 1, maxHeight: '300px', overflowY: 'auto' }}>
            <Typography variant="h6" gutterBottom>Insurance Claims</Typography>

            <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleOpenAddDialog}
                sx={{ mb: 2 }}
            >
                Add Claim
            </Button>

            <TableContainer >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Provider</TableCell>
                            <TableCell align="right">Claimed</TableCell>
                            <TableCell align="right">Approved</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Date Filed</TableCell>
                            <TableCell>Date Approved</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {claims.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">No claims filed yet.</TableCell>
                            </TableRow>
                        ) : (
                            claims.map((claim) => (
                                <TableRow 
                                    key={claim.policyId}
                                    sx={{
                                        opacity: claim._markedForDeletion ? 0.4 : 1,
                                        textDecoration: claim._markedForDeletion ? 'line-through' : 'none'
                                    }}
                                >
                                    <TableCell>
                                        {claim._markedForDeletion && (
                                            <Typography
                                                component="span"
                                                sx={{
                                                    color: '#fff',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'bold',
                                                    mr: 1,
                                                    px: 0.5,
                                                    py: 0.25,
                                                    backgroundColor: '#d32f2f',
                                                    borderRadius: 0.5,
                                                    display: 'inline-block',
                                                    opacity: 1
                                                }}
                                            >
                                                MARKED FOR DELETION
                                            </Typography>
                                        )}
                                        {claim.provider}
                                    </TableCell>
                                    <TableCell align="right">{formatAmountForDisplayWithPreferences(+claim.claimAmount || 0, preferences)}</TableCell>
                                    <TableCell align="right">{formatAmountForDisplayWithPreferences(+claim.approvedAmount || 0, preferences)}</TableCell>
                                    <TableCell>{claim.status}</TableCell>
                                    <TableCell>{claim.fileDate ? formatDateWithTimezone(claim.fileDate) : 'N/A'}</TableCell>
                                    <TableCell>{claim.approvedDate ? formatDateWithTimezone(claim.approvedDate) : 'N/A'}</TableCell>
                                    
                                    <TableCell align="center">
                                        <Can section='invoice' action='edit-invoice'>
                                            <IconButton size="small" onClick={() => handleOpenEditDialog(claim)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Can>
                                        <Can section='invoice' action='delete-payment'>
                                            <IconButton size="small" onClick={() => handleDeleteClaim(claim.id)}>
                                                <DeleteIcon fontSize="small" color="error" />
                                            </IconButton>
                                        </Can>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Typography variant="h6">Total Insurance Approved: {formatAmountForDisplayWithPreferences(totalInsuranceApproved, preferences)}</Typography>
            </Box>


            {/* Add/Edit Insurance Claim Dialog */}
            <AddEditClaimDialog
                open={openDialog}
                onClose={handleCloseDialog}
                claimData={editingClaim}
                onSave={handleSaveClaim}
                customer={customer} // Pass customer data to the dialog
                amountToClaim={amountToClaim} // Pass amount to claim
            />
        </Box>
    );
}